import type React from "react";
import { twJoin } from "tailwind-merge";

import type { VideoDto } from "@/api/types";
import { MediaXButton } from "@/components/Media/MediaXButton";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded } from "@/components/VideoInput/useVideoInput";
import { buttonVariants } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { formatFileSize } from "@/helpers/file-size";
import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { ClapperboardIcon } from "../Icons/ClapperboardIcon";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { VideoModal } from "../VideoModal/VideoModal";

interface VideoFilePreviewProps {
  video: FormVideo | VideoDto;
  isInvalid?: boolean;
  isPlayable?: boolean;
  onOpen?: (videoId: string) => void;
  onDelete?: () => void;
}
export function VideoFilePreview({
  video,
  isInvalid,
  isPlayable = true,
  onOpen,
  onDelete,
}: VideoFilePreviewProps): React.ReactNode {
  const { t } = useTranslation();
  const [isVideoModalOpened, videoModalOpenHandler] = useBool(false);
  const getFileMetadataLabel = (size: number) => {
    const formattedFileSize = formatFileSize(size, 0);

    return formattedFileSize.toString();
  };
  const Component = isPlayable ? "button" : "div";
  let fileName: string | undefined;
  let fileSize: number | undefined;
  let fileSrc: string;
  let uploadProgress: undefined | number = undefined;
  let isThumbnailShown = false;
  if (isVideoUploaded(video)) {
    fileName = video.fileName;
    fileSize = video.fileSize;
    fileSrc = video.url;
    uploadProgress = 100;
  } else {
    fileName = video.file.name;
    fileSize = video.file.size;
    fileSrc = video.url;
    isThumbnailShown = true;
    if (isDefined(video.uploadProgress)) {
      uploadProgress = video.uploadProgress;
    }
  }
  const handleOpenVideo = () => {
    videoModalOpenHandler.setTrue();
    if (isVideoUploaded(video)) {
      onOpen?.(video.id);
    }
  };

  return (
    <>
      <article
        className={twJoin(
          "flex max-h-media-input w-fit max-w-full min-w-0 items-center rounded-lg border bg-background-secondary text-left transition-colors",
          isInvalid
            ? "border-border-danger! focus-within:ring-4 focus-within:ring-border-danger-tertiary"
            : "border-transparent",
          isPlayable ? "hocus:bg-background-tertiary/50" : undefined,
        )}
      >
        <Component
          className={twResolve(
            isPlayable && buttonVariants({ variant: "subtle", type: "button" }),
            "flex h-auto! min-w-0 flex-1 shrink items-center gap-space-md overflow-visible p-space-md whitespace-normal",
            isPlayable && "cursor-pointer",
          )}
          type={isPlayable ? "button" : undefined}
          onClick={isPlayable ? handleOpenVideo : undefined}
        >
          {isThumbnailShown ? (
            <video
              className="aspect-video h-icon-display shrink-0 overflow-hidden rounded-sm bg-background-default-inverse object-contain"
              src={fileSrc}
            />
          ) : (
            <ClapperboardIcon size={32} />
          )}
          <section className="min-w-0 flex-1">
            <Stack gap="xs">
              <Text variant="body-emphasis" measure="compact" overflow="truncate" align="start">
                {fileName ?? "..."}
              </Text>
              {isDefined(uploadProgress) && uploadProgress < 100 && (
                <div className="w-full py-space-xs">
                  <ProgressBar progress={uploadProgress} />
                </div>
              )}
              {(!isDefined(uploadProgress) || uploadProgress === 100) && isDefined(fileSize) && (
                <Text as="p" variant="caption" tone="secondary" align="start">
                  {getFileMetadataLabel(fileSize)}
                </Text>
              )}
            </Stack>
          </section>
        </Component>
        {onDelete && (
          <div className="mr-space-md ml-space-sm shrink-0">
            <MediaXButton onClick={onDelete} title={t("common.action.delete")} />
          </div>
        )}
      </article>

      <VideoModal isOpened={isVideoModalOpened} videoSrc={fileSrc} onOpenChange={videoModalOpenHandler.set} />
    </>
  );
}
